import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SESSION_OPTIONS } from "../lib/data";
import { openWhatsApp } from "../lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
    name: "",
    session_type: "",
    approximate_date: "",
    message: "",
};

export default function Contact() {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);

    const onChange = (key) => (e) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.session_type || !form.message) {
            toast.error("Por favor, rellena los campos obligatorios.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("¡Mensaje enviado! Te responderé en menos de 24h.");

            const waText =
                `Hola Silvia, soy ${form.name}. ` +
                `Me interesa una sesión de: ${form.session_type}.` +
                (form.approximate_date
                    ? ` Fecha aproximada: ${form.approximate_date}.`
                    : "") +
                `\n\n${form.message}`;
            setTimeout(() => openWhatsApp(waText), 600);

            setForm(initialForm);
        } catch (err) {
            console.error(err);
            toast.error(
                "No se pudo enviar. Inténtalo de nuevo o escríbeme por WhatsApp.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contacto"
            data-testid="contact-section"
            className="relative py-14 sm:py-20 bg-[#FAFAF8]"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                    <div className="reveal">
                        <span className="text-[10px] tracking-[0.4em] uppercase text-[#C4A882]">
                            Contacto
                        </span>
                        <h2
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#1C1A18] mt-4 leading-[1.1]"
                            data-testid="contact-title"
                        >
                            Hablemos de tu historia
                        </h2>
                        <p
                            className="mt-6 text-[#6b665f] font-light max-w-md"
                            data-testid="contact-subtitle"
                        >
                            Cuéntame qué tienes en mente. Sin compromiso, sin
                            prisas. Respondo en menos de 24 horas.
                        </p>

                        <div className="mt-12 p-8 bg-[#1C1A18] text-[#FAFAF8]" data-cursor-dark="true">
                            <p className="font-display text-2xl leading-snug">
                                ¿Prefieres escribirme directamente?
                            </p>
                            <p className="text-sm text-white/60 mt-3 font-light">
                                Estoy en WhatsApp y respondo rápido. Sin
                                formularios, sin esperas.
                            </p>
                            <button
                                type="button"
                                onClick={() =>
                                    openWhatsApp(
                                        "Hola Silvia, me gustaría hablar contigo directamente.",
                                    )
                                }
                                data-testid="contact-whatsapp-direct-btn"
                                className="mt-6 inline-flex items-center gap-3 px-7 py-3.5 btn-wa text-[11px] uppercase"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="-1 -1 26 26"
                                    fill="currentColor"
                                    aria-hidden="true"
                                    style={{ display: "block" }}
                                >
                                    <path d="M19.077 4.928A9.94 9.94 0 0012.012 2C6.486 2 1.989 6.494 1.985 12.018a9.94 9.94 0 001.337 4.998L2 22l5.13-1.345a9.97 9.97 0 004.778 1.218h.004c5.526 0 10.022-4.494 10.026-10.018a9.94 9.94 0 00-2.861-7.927zM12.012 20.18h-.003a8.31 8.31 0 01-4.234-1.158l-.304-.18-3.045.799.812-2.967-.198-.314a8.27 8.27 0 01-1.27-4.412c.002-4.598 3.745-8.34 8.347-8.34 2.228 0 4.323.87 5.898 2.448a8.28 8.28 0 012.443 5.896c-.002 4.598-3.745 8.34-8.346 8.34zm4.578-6.247c-.25-.125-1.486-.733-1.716-.817-.23-.084-.398-.125-.566.125-.167.25-.65.817-.797.984-.146.168-.293.188-.543.063-.25-.125-1.058-.39-2.016-1.245-.745-.665-1.248-1.485-1.395-1.735-.146-.25-.016-.385.11-.51.112-.112.25-.293.376-.439.125-.146.166-.25.25-.418.084-.167.042-.313-.02-.438-.063-.125-.566-1.364-.776-1.867-.205-.49-.412-.424-.566-.432-.146-.007-.314-.009-.481-.009a.93.93 0 00-.67.314c-.231.25-.881.86-.881 2.099 0 1.24.902 2.436 1.028 2.604.125.167 1.776 2.713 4.302 3.804.6.26 1.07.413 1.435.529.604.192 1.153.165 1.587.1.484-.072 1.486-.608 1.696-1.195.21-.586.21-1.089.147-1.194-.063-.105-.23-.168-.48-.293z" />
                                </svg>
                                +34 688 642 601
                            </button>
                        </div>
                    </div>

                    <form
                        onSubmit={onSubmit}
                        className="reveal reveal-delay-2 space-y-7"
                        data-testid="contact-form"
                        noValidate
                    >
                        <div>
                            <label
                                htmlFor="contact-name"
                                className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2"
                            >
                                Nombre *
                            </label>
                            <input
                                id="contact-name"
                                type="text"
                                value={form.name}
                                onChange={onChange("name")}
                                className="field"
                                placeholder="Tu nombre"
                                required
                                data-testid="contact-input-name"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="contact-session"
                                className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2"
                            >
                                Tipo de sesión *
                            </label>
                            <select
                                id="contact-session"
                                value={form.session_type}
                                onChange={onChange("session_type")}
                                className="field cursor-pointer"
                                required
                                data-testid="contact-select-session"
                            >
                                <option value="">Selecciona una opción</option>
                                {SESSION_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label
                                htmlFor="contact-date"
                                className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2"
                            >
                                Fecha aproximada
                            </label>
                            <input
                                id="contact-date"
                                type="text"
                                value={form.approximate_date}
                                onChange={onChange("approximate_date")}
                                className="field"
                                placeholder="Ej: Junio 2026"
                                data-testid="contact-input-date"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="contact-message"
                                className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2"
                            >
                                Mensaje *
                            </label>
                            <textarea
                                id="contact-message"
                                rows={4}
                                value={form.message}
                                onChange={onChange("message")}
                                className="field"
                                placeholder="Cuéntame tu historia…"
                                required
                                data-testid="contact-textarea-message"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="contact-submit-btn"
                            className="w-full sm:w-auto px-10 py-4 btn-gold text-[11px] uppercase disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? "Enviando…" : "Enviar mensaje"}
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
}
